const vivaHeaders = new WeakSet();
const vivaAutoControls = new WeakSet();
const initVivaHeader = () => {
  const headers = document.querySelectorAll('[data-viva-header]');
  const commerceEnabled = document.documentElement.dataset.vivaCommerce === 'true';

  headers.forEach((header) => {
    if (vivaHeaders.has(header)) return;
    vivaHeaders.add(header);
    const toggle = header.querySelector('[data-viva-header-toggle]');
    const nav = header.querySelector('[data-viva-header-nav]');
    const desktopBreakpoint = 1099;
    const condenseThreshold = 112;
    const expandThreshold = 16;

    const syncCondensed = () => {
      if (window.innerWidth <= desktopBreakpoint) {
        header.setAttribute('data-condensed', 'false');
        return;
      }

      const isCondensed = header.getAttribute('data-condensed') === 'true';

      if (!isCondensed && window.scrollY > condenseThreshold) {
        header.setAttribute('data-condensed', 'true');
      }

      if (isCondensed && window.scrollY <= expandThreshold) {
        header.setAttribute('data-condensed', 'false');
      }
    };

    if (!toggle || !nav) return;

    const closeMenu = () => {
      header.setAttribute('data-menu-open', 'false');
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('viva-mobile-menu-open');
      document.body.classList.remove('viva-mobile-menu-open');
    };

    const openMenu = () => {
      closeSearch();
      closeDropdowns(null);
      header.setAttribute('data-menu-open', 'true');
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('viva-mobile-menu-open');
      document.body.classList.add('viva-mobile-menu-open');
    };

    toggle.addEventListener('click', () => {
      const isOpen = header.getAttribute('data-menu-open') === 'true';

      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    nav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeMenu);
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > desktopBreakpoint) {
        closeMenu();
      }

      syncCondensed();
    });

    window.addEventListener('scroll', syncCondensed, { passive: true });
    syncCondensed();

    // --- Header search toggle + predictive suggestions ---
    const searchToggle = header.querySelector('[data-viva-search-toggle]');
    const searchPanel = header.querySelector('[data-viva-search-panel]');
    const searchInput = header.querySelector('[data-viva-search-input]');
    const searchResults = header.querySelector('[data-viva-search-results]');

    const closeSearch = () => {
      if (!searchPanel) return;
      searchPanel.setAttribute('data-open', 'false');
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
    };

    if (searchToggle && searchPanel) {
      searchToggle.addEventListener('click', () => {
        const isOpen = searchPanel.getAttribute('data-open') === 'true';
        if (isOpen) {
          closeSearch();
        } else {
          closeMenu();
          searchPanel.setAttribute('data-open', 'true');
          searchToggle.setAttribute('aria-expanded', 'true');
          if (searchInput instanceof HTMLInputElement) searchInput.focus();
        }
      });
    }

    if (searchInput instanceof HTMLInputElement && searchResults) {
      let debounce = null;
      const searchCurrency = searchResults.getAttribute('data-search-currency') || 'PLN';
      const escapeHtml = (value) =>
        String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ({
          '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        }[ch]));
      const formatPrice = (raw) => {
        const value = parseFloat(raw);
        if (!isFinite(value) || value <= 0) return '';
        return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: searchCurrency }).format(value);
      };

      // Combobox semantics for the suggestion list.
      if (!searchResults.id) searchResults.id = 'viva-search-results';
      searchResults.setAttribute('role', 'listbox');
      searchInput.setAttribute('role', 'combobox');
      searchInput.setAttribute('aria-autocomplete', 'list');
      searchInput.setAttribute('aria-controls', searchResults.id);
      searchInput.setAttribute('aria-expanded', 'false');

      const setExpanded = (open) => searchInput.setAttribute('aria-expanded', open ? 'true' : 'false');

      const renderSuggestions = (products) => {
        if (!products.length) {
          searchResults.hidden = true;
          searchResults.innerHTML = '';
          setExpanded(false);
          return;
        }
        searchResults.innerHTML = products
          .map((p) => {
            const imgUrl = (p.featured_image && p.featured_image.url) || p.image || '';
            const img = imgUrl
              ? `<img class="viva-search-suggestion__image" src="${escapeHtml(imgUrl)}" alt="" width="48" height="48" loading="lazy">`
              : '<span class="viva-search-suggestion__image viva-search-suggestion__image--empty" aria-hidden="true"></span>';
            const priceText = commerceEnabled ? formatPrice(p.price) : '';
            const price = priceText ? `<span class="viva-search-suggestion__price">${escapeHtml(priceText)}</span>` : '';
            return `<a class="viva-search-suggestion" href="${escapeHtml(p.url)}" role="option">
                ${img}
                <span class="viva-search-suggestion__title">${escapeHtml(p.title)}</span>
                ${price}
              </a>`;
          })
          .join('');
        searchResults.hidden = false;
        setExpanded(true);
      };

      const fetchSuggestions = (term) => {
        const params = new URLSearchParams({
          q: term,
          'resources[type]': 'product',
          'resources[limit]': '8',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
        });
        fetch(`/search/suggest.json?${params.toString()}`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((data) => {
            const products = (data.resources && data.resources.results && data.resources.results.products) || [];
            renderSuggestions(products);
          })
          .catch(() => {
            searchResults.hidden = true;
          });
      };

      searchInput.addEventListener('input', () => {
        const term = searchInput.value.trim();
        if (debounce) window.clearTimeout(debounce);
        if (term.length < 2) {
          searchResults.hidden = true;
          searchResults.innerHTML = '';
          setExpanded(false);
          return;
        }
        debounce = window.setTimeout(() => fetchSuggestions(term), 220);
      });
    }

    // --- Dropdown triggers (click/touch + keyboard) ---
    const dropdownTriggers = header.querySelectorAll('[data-viva-dropdown-trigger]');

    const closeDropdowns = (except) => {
      dropdownTriggers.forEach((trigger) => {
        if (trigger !== except) trigger.setAttribute('aria-expanded', 'false');
      });
    };

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        closeDropdowns(trigger);
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        closeDropdowns(null);
        closeSearch();
      }
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!header.contains(target)) {
        closeMenu();
        closeDropdowns(null);
      }
    });
  });

  // Auto-submit any control marked [data-auto-submit] (collection sort/filters).
  document.querySelectorAll('[data-auto-submit]').forEach((control) => {
    if (vivaAutoControls.has(control)) return;
    vivaAutoControls.add(control);
    control.addEventListener('change', () => {
      if (control.form && typeof control.form.requestSubmit === 'function') control.form.requestSubmit();
      else if (control.form) control.form.submit();
    });
  });
};
document.addEventListener('DOMContentLoaded', initVivaHeader);
document.addEventListener('shopify:section:load', initVivaHeader);
